import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

// Styles
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import styles from './Page.module.css';
import breakpointStyles from '../../../styles/breakpoints.module.css';

// Components
import NavigationSidebar from '../../organisms/NavigationSidebar/NavigationSidebar';
import Picto from '../../atoms/Picto/Picto';
import PageHeader, {
  PageHeaderGroup,
} from '../../organisms/PageHeader/PageHeader';
import ModalTrigger from '../../molecules/ModalTrigger/ModalTrigger';
import BreadCrumb from '../../organisms/BreadCrumb/BreadCrumb';
import { PopoverContent, PopoverTrigger } from '../../atoms/Popover/Popover';
import FilterSelect from '../../molecules/FilterSelect/FilterSelect';
import Badge from '../../atoms/Badge/Badge';
import MobileCollapsible from '../../molecules/MobileCollapsible/MobileCollapsible';

// Filters
import FilterDateRangePicker from '../../organisms/FilterDateRangePicker/FilterDateRangePicker';
import FilterConditionalFilters from '../../organisms/FilterConditionalFilters/FilterConditionalFilters';
import FilterPersonalViews from '../../organisms/FilterPersonalViews/FilterPersonalViews';

// Store
import { Popover } from '../../../store/popover/PopoverContext';

// Utils
import cn from '../../../utils/cn';

// Domain
import useDateRange from '../../../domain/dateRange/useDateRange';
import { formatGlobalDateRangeLabel } from '../../../domain/dateRange/useDateRangeUseCase';
import useConditionalFilters from '../../../domain/tableConditionalFilter/useConditionalFilters';
import useTablePersonalView from '../../../domain/tablePersonalView/useTablePersonalView';
import {
  extractDateRangeForPost,
  mapApiDateRangeToUiRange,
  buildDateRangePostPayload,
  navigateSelectedDate,
  parseApiDateString,
} from '../../../domain/globalFilters/globalFiltersUseCase';
import useTablePagination from '../../../domain/tablePagination/useTablePagination';

// Hooks
import { useMobileReveal } from '../../../hooks/useMobileReveal';
import { useContexte } from '../../../hooks/useContexte';

// Store
import { FormPanelProvider, useFormPanelContext } from '../../../store/formPanel/FormPanelContext';

// Services
import { useManagerLayout, useMenuMutation } from '../../../services/layout/layoutQueries';
import { GLOBAL_FILTERS_QUERY_KEY, useGlobalFilters } from '../../../services/globalFilters/globalFiltersQueries';
import { useGlobalFiltersOptimistic } from '../../../services/globalFilters/globalFiltersOptimistic';
import { useResetFiltersMutation } from '../../../services/resetFilters/resetFiltersQueries';
import { TABLE_QUERY_KEY } from '../../../services/table/tableQueries';
import {
  TIMELINE_FILTERS_QUERY_KEY,
  TIMELINE_QUERY_KEY,
} from '../../../services/calendar/calendarQueries';
import { TABLE_FILTERS_QUERY_KEY } from '../../../services/table-filters/tableFiltersQueries';
import { DASHBOARD_QUERY_KEY } from '../../../services/dashboard/dashboardQueries';
import FormPanelStack from '../../molecules/FormPanelStack/FormPanelStack';

/**
 *
 * @param {object} param0
 * @param {ApiPage} param0.page
 * @param {React.ReactNode} [param0.children]
 * @param {string} [param0.className='']
 * @returns {React.ReactNode}
 */
function Page({
  page, children, className = '',
}) {
  return (
    <FormPanelProvider>
      <PageContent page={page} className={className}>
        {children}
      </PageContent>
    </FormPanelProvider>
  );
}

/**
 * @param {object} param0
 * @param {ApiPage} param0.page
 * @param {React.ReactNode} [param0.children]
 * @param {string} [param0.className='']
 * @returns {React.ReactNode}
 */
function PageContent({
  page, children, className = '',
}) {
  const template = page?.template;
  const { t } = useTranslation();

  const { handleOpenModal, handleCloseModal } = useFormPanelContext();
  const {
    isOpen: isMobileGlobalFiltersOpen,
    onToggleClick: onMobileGlobalFiltersToggleClick,
  } = useMobileReveal({ initialOpen: false });
  const queryClient = useQueryClient();
  const contexte = useContexte();

  const handleFormCloseAfterSuccess = useCallback(() => {
    handleCloseModal();
    queryClient.invalidateQueries({ queryKey: [GLOBAL_FILTERS_QUERY_KEY], type: 'active' });
    if (template === 'table' || page?.format === 'table') {
      queryClient.invalidateQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
    }
    if (template === 'dashboard') {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEY], type: 'active' });
    }
    if (template === 'timeline') {
      queryClient.invalidateQueries({ queryKey: [TIMELINE_FILTERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });
    }
  }, [handleCloseModal, queryClient, template]);

  const { mutate: saveMenu, isPending: isSaveMenuPending } = useMenuMutation();

  const { data: managerLayout, isPending: isManagerLayoutPending } = useManagerLayout(
    { path: page?.path, contexte },
    {
      enabled: !!page?.path,
      ...(template === 'detail' ? { placeholderData: keepPreviousData } : {}),
    },
  );

  const {
    data: managerGlobalFilters,
    isPending: isManagerGlobalFiltersPending,
    isRefetching: isManagerGlobalFiltersRefetching,
  } = useGlobalFilters(
    { path: page?.path },
    {
      enabled: !!page?.path,
      ...(template === 'detail' ? { placeholderData: keepPreviousData } : {}),
    },
  );

  const { resetPageToFirst } = useTablePagination();

  const { mutate: resetView, isPending: isResetViewPending } = useResetFiltersMutation({
    onSuccess: () => {
      resetPageToFirst();
      queryClient.invalidateQueries({ queryKey: [GLOBAL_FILTERS_QUERY_KEY, page?.path] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_FILTERS_QUERY_KEY, page?.path] });
    },
  });

  const managerDateRange = managerGlobalFilters?.dateRange;
  const type = managerDateRange?.type;
  const typeOptions = managerDateRange?.typeOptions;
  const filters = managerGlobalFilters?.filters;
  const filterValues = managerGlobalFilters?.filterValues;
  const managerPersonalViews = managerGlobalFilters?.personalViews;
  const personalViewsPath = managerGlobalFilters?.personalViewsPath;
  const personalViewsFormUrl = managerGlobalFilters?.personalViewsFormUrl;
  const ranges = useMemo(
    () => mapApiDateRangeToUiRange(managerDateRange),
    [managerDateRange?.rangeStartDate, managerDateRange?.rangeEndDate, isManagerGlobalFiltersPending],
  );

  const selectedDate = useMemo(
    () => parseApiDateString(managerDateRange?.selectedDate) ?? ranges[0]?.startDate,
    [managerDateRange?.selectedDate, ranges],
  );

  // Domain - Date Range
  const dateRange = useDateRange({
    initialRanges: ranges,
    type,
    typeOptions,
    isLoading: isManagerGlobalFiltersPending,
    selectedDate,
  });
  const {
    calendarOpen,
    setCalendarOpen,
    typeOptionsList,
    handlePeriodArrows,
    handleTypeChange,
  } = dateRange;

  // Domain - Table Conditional Filters
  const conditionalFilters = useConditionalFilters({
    filters,
    initialValues: filterValues,
    isLoading: isManagerGlobalFiltersRefetching,
  });
  const {
    // Conditional Filters - Popover
    popoverConditionalFiltersIsOpen,
    setPopoverConditionalFiltersIsOpen,

    // Conditional Filters - Count
    getConditionalFiltersCount,
  } = conditionalFilters;

  // Domain - Table Personal View
  const personalViews = useTablePersonalView({
    personalViews: managerPersonalViews,
    isLoading: isManagerGlobalFiltersPending,
  });
  const {
    selectedPersonalViewItem,
    popoverPersonalViewIsOpen,
    setPopoverPersonalViewIsOpen,
  } = personalViews;

  // Mutation - Global Filters
  const {
    mutate: mutateGlobalFiltersOptimistic,
    isPending: isMutateGlobalFiltersOptimisticPending,
  } = useGlobalFiltersOptimistic(page?.path, {});

  const {
    mutate: mutateGlobalFiltersPersonalViews,
    isPending: isMutateGlobalFiltersPersonalViewsPending,
  } = useGlobalFiltersOptimistic(page?.path, {});

  const handleDateRangeMutation = (
    /** @type {ApiManagerDateRangeType} */ newType,
    /** @type {import('react-date-range').Range} */ newRange,
    /** @type {Date} */ newSelectedDate,
  ) => {
    if (!newRange) return;
    resetPageToFirst();
    const selected = newSelectedDate ?? selectedDate ?? newRange.startDate;
    mutateGlobalFiltersOptimistic({
      dateRange: buildDateRangePostPayload({
        type: newType,
        range: newRange,
        selectedDate: selected,
      }),
      filterValues: managerGlobalFilters?.filterValues ?? [],
      personalViews: { selectedOption: '' },
    });
  };

  const handlePrevNextValueMutation = (/** @type {DateRangeDirection} */ direction) => {
    const newRange = handlePeriodArrows(direction);
    const newSelectedDate = navigateSelectedDate(selectedDate, direction, type);
    handleDateRangeMutation(type, newRange, newSelectedDate);
  };

  const handleTypeValueChangeMutation = (/** @type {ApiManagerDateRangeType} */ newType) => {
    const newRange = handleTypeChange(newType);
    handleDateRangeMutation(newType, newRange, selectedDate);
  };

  const handleConditionalFiltersMutation = (/** @type {Array<ApiManagerFilterValues>} */ data) => {
    resetPageToFirst();
    mutateGlobalFiltersOptimistic({
      dateRange: extractDateRangeForPost(managerGlobalFilters?.dateRange),
      filterValues: data,
      personalViews: { selectedOption: '' },
    }, {
      onSuccess: () => {
        setPopoverConditionalFiltersIsOpen(false);
      },
    });
  };

  const handlePersonalViewsSelectedOptionMutation = (/** @type {string} */ selectedOption) => {
    resetPageToFirst();
    mutateGlobalFiltersPersonalViews({
      dateRange: extractDateRangeForPost(managerGlobalFilters?.dateRange),
      filterValues: managerGlobalFilters?.filterValues ?? [],
      personalViews: { selectedOption },
    });
  };

  return (
    <div
      className={cn([
        styles.wrapper,
        className,
      ])}
    >
      <NavigationSidebar
        user={managerLayout?.user}
        menu={managerLayout?.menu}
        isLoading={isManagerLayoutPending}
        onSaveMenu={(nextItems, { optimistic, onSuccess } = {}) => (
          saveMenu({
            nextItems,
            bottomSectionItems: managerLayout?.menu?.bottomSection?.items,
            optimistic,
          }, { onSuccess })
        )}
        isSavingMenu={isSaveMenuPending}
      />

      <div className={cn([styles.body, styles[template]])}>
        <PageHeader isSticky={template !== 'table' && template !== 'timeline'} className={styles.header}>
          <div className={styles['page-header-stack']}>
            <div className={styles['page-header-top-row']}>
              <PageHeaderGroup className={styles['page-header-breadcrumb']}>
                <BreadCrumb
                  items={managerLayout?.breadcrumb}
                  template={template}
                  isLoading={isManagerLayoutPending}
                />
              </PageHeaderGroup>
              <span className={breakpointStyles['mobile-only']}>
                <button
                  type="button"
                  className={breakpointStyles['mobile-filter-toggle']}
                  onClick={onMobileGlobalFiltersToggleClick}
                  aria-expanded={isMobileGlobalFiltersOpen}
                  aria-controls="page-global-filters"
                >
                  <Picto
                    icon="chevron-down-range"
                    className={cn([
                      breakpointStyles['mobile-filter-toggle-icon'],
                      isMobileGlobalFiltersOpen && breakpointStyles['mobile-filter-toggle-icon-open'],
                    ])}
                    aria-hidden
                  />
                  <span>
                    {isMobileGlobalFiltersOpen
                      ? t('table.filters.hideMobile')
                      : t('table.filters.toggleMobile')}
                  </span>
                </button>
              </span>
            </div>
            <MobileCollapsible
              id="page-global-filters"
              isOpen={isMobileGlobalFiltersOpen}
              className={styles['page-header-filters-collapsible']}
            >
              <PageHeaderGroup className={styles['page-header-filters']}>
                {/* Date Range Picker Popover */}
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen} placement="bottom">
                  <PopoverTrigger asChild>
                    <ModalTrigger
                      icon="calendar-main"
                      iconPosition="right"
                      title={t('date.openCalendar')}
                      label={formatGlobalDateRangeLabel({
                        type,
                        startDate: ranges[0].startDate,
                        endDate: ranges[0].endDate,
                      }, t)}
                      onClick={() => setCalendarOpen(!calendarOpen)}
                      template={template}
                      isLoading={isManagerGlobalFiltersPending}
                      disabled={isMutateGlobalFiltersPersonalViewsPending}
                      maxWidth="420px"
                    />
                  </PopoverTrigger>
                  <PopoverContent>
                    <FilterDateRangePicker
                      dateRange={dateRange}
                      ranges={ranges}
                      type={type}
                      selectedDate={selectedDate}
                      handleDateRangeMutation={handleDateRangeMutation}
                    />
                  </PopoverContent>
                </Popover>

                {/* Date Range Filter Select */}
                <FilterSelect
                  options={typeOptionsList}
                  value={type}
                  handleValue={(/** @type {ApiManagerDateRangeType} */ value) => handleTypeValueChangeMutation(value)}
                  hasPrevNext={type !== 'custom' && type !== 'all'}
                  handlePrevNextValue={(direction) => handlePrevNextValueMutation(direction)}
                  template={template}
                  isLoading={isManagerGlobalFiltersPending}
                  disabled={isMutateGlobalFiltersPersonalViewsPending}
                >
                  {(option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  )}
                </FilterSelect>

                {/* Conditional Filters Popover */}
                <Popover
                  open={popoverConditionalFiltersIsOpen}
                  onOpenChange={setPopoverConditionalFiltersIsOpen}
                  placement="bottom-start"
                >
                  <PopoverTrigger asChild>
                    <ModalTrigger
                      icon="filter"
                      title={t('table.filters.others')}
                      label={t('table.filters.others')}
                      onClick={() => setPopoverConditionalFiltersIsOpen(
                        !popoverConditionalFiltersIsOpen,
                      )}
                      template={template}
                      maxWidth="150px"
                      isLoading={isManagerGlobalFiltersPending}
                      disabled={isMutateGlobalFiltersPersonalViewsPending}
                    >
                      {getConditionalFiltersCount(filterValues) > 0
                        ? <Badge value={getConditionalFiltersCount(filterValues)} />
                        : null}
                    </ModalTrigger>
                  </PopoverTrigger>
                  <PopoverContent>
                    <FilterConditionalFilters
                      isLoading={isManagerGlobalFiltersPending}
                      isSubmitting={isMutateGlobalFiltersOptimisticPending}
                      onSubmit={(data) => handleConditionalFiltersMutation(data)}
                      conditionalFilters={conditionalFilters}
                    />
                  </PopoverContent>
                </Popover>

                {/* Personal Views Popover */}
                <Popover
                  open={popoverPersonalViewIsOpen}
                  onOpenChange={setPopoverPersonalViewIsOpen}
                  placement="bottom-start"
                >
                  <PopoverTrigger asChild>
                    <ModalTrigger
                      icon={popoverPersonalViewIsOpen ? 'unfold-less' : 'unfold-more'}
                      iconPosition="right"
                      title={selectedPersonalViewItem?.lib || t('table.filters.personalViews')}
                      label={selectedPersonalViewItem?.lib || t('table.filters.personalViews')}
                      onClick={() => setPopoverPersonalViewIsOpen(!popoverPersonalViewIsOpen)}
                      template={template}
                      isLoading={isManagerGlobalFiltersPending}
                      maxWidth="150px"
                    />
                  </PopoverTrigger>
                  <PopoverContent>
                    <FilterPersonalViews
                      mutationKey={GLOBAL_FILTERS_QUERY_KEY}
                      path={page?.path}
                      personalViews={personalViews}
                      personalViewsPath={personalViewsPath}
                      personalViewsFormUrl={personalViewsFormUrl}
                      handlePersonalViewsSelectedOptionMutation={handlePersonalViewsSelectedOptionMutation}
                      handleOpenModal={handleOpenModal}
                      managerPersonalViews={managerPersonalViews}
                      contexte={contexte}
                      onResetView={() => resetView({ path: page?.path, type: 'globalFilters' })}
                      isResetting={isResetViewPending}
                    />
                  </PopoverContent>
                </Popover>
              </PageHeaderGroup>
            </MobileCollapsible>
          </div>
        </PageHeader>
        <FormPanelStack onRootCloseAfterSuccess={handleFormCloseAfterSuccess} />
        <div className={styles.main}>{children ?? null}</div>
      </div>
    </div>
  );
}

export default Page;
